/**
 * TEMA OFICIAL TERAPPY - Material-UI v5
 * 
 * Implementa la identidad corporativa oficial de Terappy según el
 * Manual de Identidad Gráfica.
 * 
 * Colores: Negro #000000 + Verde Lima #B3CC1D, #E0E50A, #EFF284
 * Tipografía: Playfair Display (headings) + Open Sans (body)
 * 
 * Referencias:
 * - /workspaces/terappy-backend/docs/BRAND_IDENTITY_GUIDE.md
 * - /workspaces/terappy-app/docs/CURRENT_TECH_STACK.md
 * 
 * @version 1.0.0
 * @date 2026-02-16
 */

import { createTheme } from '@mui/material/styles';

// ============================================
// DESIGN TOKENS - IDENTIDAD CORPORATIVA
// ============================================

/**
 * Colores de Marca (Brand Colors)
 * Fuente: Manual de Identidad Gráfica Terappy
 */
const brandColors = {
  // Colores principales
  black: '#000000',           // Negro Corporativo (CMYK: 91%,79%,62%,97%)
  greenPrimary: '#B3CC1D',    // Verde Lima Principal (RGB: 179,204,29)
  greenVibrant: '#E0E50A',    // Verde Lima Vibrante (RGB: 224,229,10)
  greenLight: '#EFF284',      // Verde Lima Claro (RGB: 239,242,132)
  
  // Neutrales (escala de grises)
  white: '#FFFFFF',
  gray50: '#FAFAFA',
  gray100: '#F5F5F5',
  gray200: '#EEEEEE',
  gray300: '#E0E0E0',
  gray400: '#BDBDBD',
  gray500: '#9E9E9E',
  gray600: '#757575',
  gray700: '#616161',
  gray800: '#424242',
  gray900: '#212121',
};

/**
 * Colores Semánticos (Estados del Sistema)
 */
const semanticColors = {
  success: '#4CAF50',    // Verde oscuro - confirmaciones
  warning: '#FF9800',    // Naranja - advertencias
  error: '#F44336',      // Rojo - errores
  info: '#2196F3',       // Azul - información
};

/**
 * Sistema de Espaciado (8px Grid)
 */
const spacing = {
  unit: 8,
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
  xxxl: 64,
};

/**
 * Tipografía Corporativa
 * - Headings: Playfair Display
 * - Body: Open Sans
 */
const typography = {
  // Font families
  fontFamilyHeading: "'Playfair Display', Georgia, serif",
  fontFamilyBody: "'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
  fontFamilyMono: "'Fira Code', 'Courier New', Consolas, monospace",
  
  // Font weights
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemiBold: 600,
  fontWeightBold: 700,
  fontWeightBlack: 900,
  
  // Font sizes (Typography Scale)
  h1: {
    fontSize: '3rem',      // 48px
    lineHeight: 1.167,     // 56px
    fontWeight: 700,
  },
  h2: {
    fontSize: '2.25rem',   // 36px
    lineHeight: 1.222,     // 44px
    fontWeight: 700,
  },
  h3: {
    fontSize: '1.75rem',   // 28px
    lineHeight: 1.286,     // 36px
    fontWeight: 700,
  },
  h4: {
    fontSize: '1.5rem',    // 24px
    lineHeight: 1.333,     // 32px
    fontWeight: 600,
  },
  h5: {
    fontSize: '1.25rem',   // 20px
    lineHeight: 1.4,       // 28px
    fontWeight: 600,
  },
  h6: {
    fontSize: '1.125rem',  // 18px
    lineHeight: 1.333,     // 24px
    fontWeight: 600,
  },
  body1: {
    fontSize: '1rem',      // 16px
    lineHeight: 1.5,       // 24px
    fontWeight: 400,
  },
  body2: {
    fontSize: '0.875rem',  // 14px
    lineHeight: 1.429,     // 20px
    fontWeight: 400,
  },
  subtitle1: {
    fontSize: '1.125rem',  // 18px
    lineHeight: 1.556,     // 28px
    fontWeight: 400,
  },
  button: {
    fontSize: '0.875rem',  // 14px
    fontWeight: 600,
    textTransform: 'none', // No uppercase por defecto
    letterSpacing: '0.02em',
  },
  caption: {
    fontSize: '0.75rem',   // 12px
    lineHeight: 1.333,     // 16px
    fontWeight: 400,
  },
  overline: {
    fontSize: '0.625rem',  // 10px
    lineHeight: 1.2,       // 12px
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
};

/**
 * Sombras (Elevations)
 */
const shadows = {
  level1: '0 1px 3px rgba(0, 0, 0, 0.12)',
  level2: '0 2px 6px rgba(0, 0, 0, 0.16)',
  level3: '0 4px 12px rgba(0, 0, 0, 0.20)',
  level4: '0 8px 24px rgba(0, 0, 0, 0.24)',
};

/**
 * Border Radius
 */
const borderRadius = {
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  full: 9999,
};

/**
 * Breakpoints (Responsive Design)
 */
const breakpoints = {
  values: {
    xs: 0,       // Mobile
    sm: 375,     // Mobile Large
    md: 768,     // Tablet
    lg: 1280,    // Desktop
    xl: 1920,    // Large Desktop
  },
};

// ============================================
// TEMA MUI - CONFIGURACIÓN COMPLETA
// ============================================

const terappyTheme = createTheme({
  // Palette (Colores)
  palette: {
    mode: 'light',
    
    // Primary: Verde Lima (Brand)
    primary: {
      main: brandColors.greenPrimary,      // #B3CC1D
      light: brandColors.greenLight,       // #EFF284
      dark: '#9BAA1A',                     // Verde más oscuro
      contrastText: brandColors.black,     // Negro sobre verde
    },
    
    // Secondary: Negro Corporativo
    secondary: {
      main: brandColors.black,             // #000000
      light: brandColors.gray800,          // #424242
      dark: brandColors.black,
      contrastText: brandColors.white,     // Blanco sobre negro
    },
    
    // Accent: Verde Vibrante
    accent: {
      main: brandColors.greenVibrant,      // #E0E50A
      contrastText: brandColors.black,
    },
    
    // Semantic colors
    success: {
      main: semanticColors.success,
      light: '#81C784',
      dark: '#388E3C',
      contrastText: brandColors.white,
    },
    error: {
      main: semanticColors.error,
      light: '#E57373',
      dark: '#D32F2F',
      contrastText: brandColors.white,
    },
    warning: {
      main: semanticColors.warning,
      light: '#FFB74D',
      dark: '#F57C00',
      contrastText: brandColors.black,
    },
    info: {
      main: semanticColors.info,
      light: '#4FC3F7',
      dark: '#0277BD',
      contrastText: brandColors.white,
    },
    
    // Neutrales
    grey: {
      50: brandColors.gray50,
      100: brandColors.gray100,
      200: brandColors.gray200,
      300: brandColors.gray300,
      400: brandColors.gray400,
      500: brandColors.gray500,
      600: brandColors.gray600,
      700: brandColors.gray700,
      800: brandColors.gray800,
      900: brandColors.gray900,
    },
    
    // Text colors
    text: {
      primary: brandColors.gray800,        // #424242
      secondary: brandColors.gray600,      // #757575
      disabled: brandColors.gray400,       // #BDBDBD
    },
    
    // Background colors
    background: {
      default: brandColors.gray50,         // #FAFAFA
      paper: brandColors.white,            // #FFFFFF
    },
    
    // Dividers
    divider: brandColors.gray200,          // #EEEEEE
  },
  
  // Typography
  typography: {
    fontFamily: typography.fontFamilyBody,
    fontSize: 16,
    fontWeightLight: typography.fontWeightLight,
    fontWeightRegular: typography.fontWeightRegular,
    fontWeightMedium: typography.fontWeightMedium,
    fontWeightBold: typography.fontWeightBold,
    
    // Headings (Playfair Display)
    h1: {
      fontFamily: typography.fontFamilyHeading,
      fontSize: typography.h1.fontSize,
      lineHeight: typography.h1.lineHeight,
      fontWeight: typography.h1.fontWeight,
      color: brandColors.black,
    },
    h2: {
      fontFamily: typography.fontFamilyHeading,
      fontSize: typography.h2.fontSize,
      lineHeight: typography.h2.lineHeight,
      fontWeight: typography.h2.fontWeight,
      color: brandColors.black,
    },
    h3: {
      fontFamily: typography.fontFamilyHeading,
      fontSize: typography.h3.fontSize,
      lineHeight: typography.h3.lineHeight,
      fontWeight: typography.h3.fontWeight,
      color: brandColors.black,
    },
    h4: {
      fontFamily: typography.fontFamilyHeading,
      fontSize: typography.h4.fontSize,
      lineHeight: typography.h4.lineHeight,
      fontWeight: typography.h4.fontWeight,
      color: brandColors.black,
    },
    h5: {
      fontFamily: typography.fontFamilyHeading,
      fontSize: typography.h5.fontSize,
      lineHeight: typography.h5.lineHeight,
      fontWeight: typography.h5.fontWeight,
      color: brandColors.black,
    },
    h6: {
      fontFamily: typography.fontFamilyHeading,
      fontSize: typography.h6.fontSize,
      lineHeight: typography.h6.lineHeight,
      fontWeight: typography.h6.fontWeight,
      color: brandColors.black,
    },
    
    // Body text (Open Sans)
    body1: {
      fontSize: typography.body1.fontSize,
      lineHeight: typography.body1.lineHeight,
      fontWeight: typography.body1.fontWeight,
    },
    body2: {
      fontSize: typography.body2.fontSize,
      lineHeight: typography.body2.lineHeight,
      fontWeight: typography.body2.fontWeight,
    },
    subtitle1: {
      fontSize: typography.subtitle1.fontSize,
      lineHeight: typography.subtitle1.lineHeight,
      fontWeight: typography.subtitle1.fontWeight,
    },
    button: {
      fontSize: typography.button.fontSize,
      fontWeight: typography.button.fontWeight,
      textTransform: typography.button.textTransform,
      letterSpacing: typography.button.letterSpacing,
    },
    caption: {
      fontSize: typography.caption.fontSize,
      lineHeight: typography.caption.lineHeight,
      fontWeight: typography.caption.fontWeight,
      color: brandColors.gray600,
    },
    overline: {
      fontSize: typography.overline.fontSize,
      lineHeight: typography.overline.lineHeight,
      fontWeight: typography.overline.fontWeight,
      textTransform: typography.overline.textTransform,
      letterSpacing: typography.overline.letterSpacing,
    },
  },
  
  // Spacing (8px Grid)
  spacing: spacing.unit,
  
  // Breakpoints
  breakpoints: breakpoints,
  
  // Shape (Border Radius)
  shape: {
    borderRadius: borderRadius.md,
  },
  
  // Shadows
  shadows: [
    'none',
    shadows.level1,
    shadows.level2,
    shadows.level3,
    shadows.level4,
    shadows.level4,
    shadows.level4,
    shadows.level4,
    shadows.level4,
    shadows.level4,
    shadows.level4,
    shadows.level4,
    shadows.level4,
    shadows.level4,
    shadows.level4,
    shadows.level4,
    shadows.level4,
    shadows.level4,
    shadows.level4,
    shadows.level4,
    shadows.level4,
    shadows.level4,
    shadows.level4,
    shadows.level4,
    shadows.level4,
  ],
  
  // ============================================
  // COMPONENT OVERRIDES - BRAND STYLING
  // ============================================
  
  components: {
    // Button
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.md,
          padding: `${spacing.md}px ${spacing.lg}px`,
          fontSize: typography.button.fontSize,
          fontWeight: typography.button.fontWeight,
          textTransform: 'none',
          transition: 'all 200ms ease',
          
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: shadows.level2,
          },
        },
        
        // Primary button (Verde Lima)
        containedPrimary: {
          backgroundColor: brandColors.greenPrimary,
          color: brandColors.black,
          
          '&:hover': {
            backgroundColor: brandColors.greenVibrant,
          },
          
          '&:active': {
            backgroundColor: '#9BAA1A',
          },
        },
        
        // Secondary button (Negro)
        containedSecondary: {
          backgroundColor: brandColors.black,
          color: brandColors.white,
          
          '&:hover': {
            backgroundColor: brandColors.gray800,
          },
        },
        
        // Outlined button
        outlined: {
          borderWidth: 2,
          borderColor: brandColors.greenPrimary,
          color: brandColors.black,
          
          '&:hover': {
            backgroundColor: brandColors.greenLight,
            borderColor: brandColors.greenVibrant,
          },
        },
        
        // Text button
        text: {
          color: brandColors.black,
          
          '&:hover': {
            color: brandColors.greenPrimary,
            backgroundColor: 'transparent',
          },
        },
        
        // Sizes
        sizeSmall: {
          padding: `${spacing.sm}px ${spacing.md}px`,
          fontSize: '0.75rem',
        },
        sizeLarge: {
          padding: `${spacing.lg}px ${spacing.xl}px`,
          fontSize: '1rem',
        },
      },
    },
    
    // TextField / Input
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: borderRadius.md,
            
            '& fieldset': {
              borderColor: brandColors.gray200,
              borderWidth: 1,
            },
            
            '&:hover fieldset': {
              borderColor: brandColors.gray400,
            },
            
            '&.Mui-focused fieldset': {
              borderColor: brandColors.greenPrimary,
              borderWidth: 2,
            },
            
            '&.Mui-error fieldset': {
              borderColor: semanticColors.error,
            },
          },
          
          '& .MuiInputLabel-root': {
            fontFamily: typography.fontFamilyHeading,
            fontWeight: typography.fontWeightSemiBold,
            fontSize: '1rem',
            
            '&.Mui-focused': {
              color: brandColors.greenPrimary,
            },
          },
        },
      },
    },
    
    // Card
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.lg,
          border: `1px solid ${brandColors.gray200}`,
          boxShadow: shadows.level2,
          transition: 'all 200ms ease',
          
          '&:hover': {
            borderColor: brandColors.greenPrimary,
            boxShadow: shadows.level3,
            transform: 'translateY(-4px)',
          },
        },
      },
    },
    
    // Paper
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: borderRadius.md,
        },
        elevation1: {
          boxShadow: shadows.level1,
        },
        elevation2: {
          boxShadow: shadows.level2,
        },
        elevation3: {
          boxShadow: shadows.level3,
        },
        elevation4: {
          boxShadow: shadows.level4,
        },
      },
    },
    
    // AppBar / Navigation
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: brandColors.black,
          color: brandColors.white,
          boxShadow: shadows.level2,
        },
        colorPrimary: {
          backgroundColor: brandColors.black,
        },
      },
    },
    
    // Chip / Badge
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.full,
          fontWeight: typography.fontWeightSemiBold,
        },
        filled: {
          backgroundColor: brandColors.greenLight,
          color: brandColors.black,
        },
        outlined: {
          borderColor: brandColors.greenPrimary,
          color: brandColors.black,
        },
        colorPrimary: {
          backgroundColor: brandColors.greenPrimary,
          color: brandColors.black,
        },
        colorSecondary: {
          backgroundColor: brandColors.black,
          color: brandColors.white,
        },
      },
    },
    
    // Alert
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.md,
          border: `1px solid`,
        },
        standardSuccess: {
          backgroundColor: '#E8F5E9',
          borderColor: brandColors.greenPrimary,
          color: '#2E7D32',
        },
        standardError: {
          backgroundColor: '#FFEBEE',
          borderColor: semanticColors.error,
        },
        standardWarning: {
          backgroundColor: '#FFF3E0',
          borderColor: semanticColors.warning,
        },
        standardInfo: {
          backgroundColor: '#E3F2FD',
          borderColor: semanticColors.info,
        },
      },
    },
    
    // Link
    MuiLink: {
      styleOverrides: {
        root: {
          color: brandColors.black,
          textDecoration: 'underline',
          
          '&:hover': {
            color: brandColors.greenPrimary,
          },
        },
      },
    },
    
    // Tooltip
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: brandColors.gray900,
          color: brandColors.white,
          fontSize: typography.caption.fontSize,
          borderRadius: borderRadius.sm,
          padding: `${spacing.sm}px ${spacing.md}px`,
        },
      },
    },
    
    // Tabs
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: typography.fontWeightRegular,
          fontSize: typography.body1.fontSize,
          color: brandColors.gray600,
          
          '&.Mui-selected': {
            color: brandColors.black,
            fontWeight: typography.fontWeightBold,
          },
        },
      },
    },
    
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: brandColors.greenPrimary,
          height: 3,
        },
      },
    },
  },
});

// Exports
export default terappyTheme;

/**
 * Export individual design tokens for use in styled-components
 */
export {
  brandColors,
  semanticColors,
  spacing,
  typography,
  shadows,
  borderRadius,
  breakpoints,
};

/**
 * Helper: Responsive font sizes
 * Usage: theme.mixins.responsiveFontSize(theme, 'h1')
 */
terappyTheme.mixins = {
  ...terappyTheme.mixins,
  
  responsiveFontSize: (variant) => {
    const sizes = {
      h1: { xs: '2rem', sm: '2.5rem', md: '3rem' },
      h2: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
      h3: { xs: '1.5rem', sm: '1.625rem', md: '1.75rem' },
      h4: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' },
      h5: { xs: '1.125rem', sm: '1.1875rem', md: '1.25rem' },
      h6: { xs: '1rem', sm: '1.0625rem', md: '1.125rem' },
    };
    
    return sizes[variant] || sizes.h6;
  },
};
